import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Table, Button, Form, Spinner, Alert, Badge, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const UtilityExpensesPage = () => {
    const navigate = useNavigate();

    const [expenses, setExpenses] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedExpenseTypeId, setSelectedExpenseTypeId] = useState("");

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken") ? `Bearer ${localStorage.getItem("authToken")}` : "",
    });

    const fetchExpenseTypes = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/expense-types?type=Utility Expense`,
                { headers: getAuthHeaders() }
            );
            setExpenseTypes(res.data?.data || res.data || []);
        } catch (err) {
            console.error("Failed to fetch expense types:", err);
        }
    };

    const fetchExpenses = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const params = {};
            if (search) params.search = search;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (selectedExpenseTypeId) params.expense_type_id = selectedExpenseTypeId;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/utility-expenses`,
                { headers: getAuthHeaders(), params }
            );

            setExpenses(response.data?.data || response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load utility expenses.");
        } finally {
            setLoading(false);
        }
    }, [search, fromDate, toDate, selectedExpenseTypeId]);

    useEffect(() => {
        fetchExpenseTypes();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => fetchExpenses(), 300);
        return () => clearTimeout(timer);
    }, [fetchExpenses]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this utility expense?")) return;

        try {
            setError("");
            setMessage("");
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/admin/utility-expenses/${id}`,
                { headers: getAuthHeaders() }
            );
            setMessage("Utility expense deleted successfully.");
            await fetchExpenses();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete expense entry.");
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Utility Expenses</h2>
                        <p className="text-muted mb-0">Manage and track company utility bills.</p>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            className="px-4 fw-semibold"
                            onClick={() => navigate("/utility-expenses/create")}
                        >
                            + Record Utility Expense
                        </Button>
                    </Col>
                </Row>

                {message && <Alert variant="success" dismissible onClose={() => setMessage("")}>{message}</Alert>}
                {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-3">
                        <Row className="g-3">
                            <Col md={3}>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search note..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="bg-light border-0 py-2"
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={3}>
                                <Form.Select
                                    value={selectedExpenseTypeId}
                                    onChange={(e) => setSelectedExpenseTypeId(e.target.value)}
                                    className="bg-light border-0 py-2"
                                >
                                    <option value="">All Utility Types</option>
                                    {expenseTypes.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-light border-0 py-2"
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-light border-0 py-2"
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-2 text-muted">Loading utility expenses...</div>
                            </div>
                        ) : expenses.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                No utility expenses recorded.
                            </div>
                        ) : (
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4">#</th>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Note</th>
                                        <th>Entered By</th>
                                        <th>Bank Account</th>
                                        <th>Amount</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td className="ps-4 fw-bold text-muted">{index + 1}</td>
                                            <td className="fw-semibold">{item.date}</td>
                                            <td>
                                                <Badge bg="info" className="text-dark">
                                                    {item.expense_type?.name || "N/A"}
                                                </Badge>
                                            </td>
                                            <td style={{ maxWidth: "200px" }} className="text-truncate">
                                                {item.note}
                                            </td>
                                            <td className="text-secondary small">
                                                {item.entered_by?.name || "N/A"}
                                            </td>
                                            <td>
                                                <Badge bg="light" className="text-dark border">
                                                    {item.payment?.bank?.name || "N/A"}
                                                </Badge>
                                            </td>
                                            <td className="fw-bold text-dark">
                                                ${parseFloat(item.amount || 0).toFixed(2)}
                                            </td>
                                            <td className="text-end pe-4">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => navigate(`/utility-expenses/edit/${item.id}`)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
};

export default UtilityExpensesPage;